import React, { useCallback, useEffect, useState } from "react";
import { AppDialog } from "components/common/dialogs";
import {
  CheckCircleOutline,
  EditNoteOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import DataTable from "../../../components/common/tables/DataTable";
import ApprovalTimeline from "../components/ApprovalTimeline";
import AssignmentDecisionDialog from "../components/AssignmentDecisionDialog";
import StatusChip from "../components/StatusChip";
import { AppButton } from "components/common/buttons";
import { AppTextField } from "components/common/inputs";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import ApprovalInfoEditDialog from "./ApprovalInfoEditDialog";
import ApprovalItemsEditDialogs from "./ApprovalItemsEditDialogs";

const displayValue = (value) => value === null || value === undefined || value === "" ? "-" : value;

export const ReviewNoteField = React.memo(function ReviewNoteField({
  label = "Lý do / ghi chú chung",
  disabled = false,
  resetKey,
  onValueChange,
}) {
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue("");
    onValueChange?.("");
  }, [onValueChange, resetKey]);

  const handleChange = useCallback((nextValue) => {
    setValue(nextValue);
    onValueChange?.(nextValue);
  }, [onValueChange]);

  return (
    <AppTextField
      label={label}
      disabled={disabled}
      multiline
      minRows={2}
      value={value}
      onChange={handleChange}
    />
  );
});

export default function ApprovalWorkflowDialogs({
  exportNotice,
  onCloseExportNotice,
  decisionDialogOpen,
  decisionForm,
  decisionTeamOptions,
  decisionError,
  decisionLoading,
  decisionDownloading,
  decisionDownloadFormat,
  onCloseDecisionDialog,
  onDownloadAssignmentDecisionPdf,
  onDownloadAssignmentDecisionWord,
  includeApprovalDialogs = false,
  includeReviewDialogs = false,
  submitDialogOpen,
  onCloseSubmitDialog,
  approvalDialogTitle,
  approvalActionLabel,
  approvalSubmitInitialForm,
  isEditingCurrentApproval,
  sections,
  items,
  userOptions,
  submitting,
  draftNotice,
  draftSelectionColumns,
  expandedItemIds,
  canSubmit,
  approvalSteps,
  groupApprovalItemsByScope,
  onAddDraftItem,
  onSubmitApproval,
  approvalInfoDialog,
  onCloseApprovalInfoDialog,
  approvalInfoInitialData,
  onSaveApprovalInfo,
  approvalInfoFields,
  draftDialog,
  onCloseDraftDialog,
  onSaveDraftItem,
  draftParentOptions,
  draftScopeItems,
  draftOfficialScopeItems,
  reviewDialog,
  onCloseReviewDialog,
  reviewPhaseTitle,
  reviewError,
  subcontract,
  reviewOverviewFields,
  showOfficialCompare,
  onToggleOfficialCompare,
  reviewDisplayItemGroups,
  reviewColumns,
  onExpandItemRows,
  onCollapseItemRows,
  markAllReviewItems,
  reviewNoteResetKey,
  updateReviewNoteRef,
  onSaveReview,
  sortedReviewDisplayRows,
}) {
  return (
    <>
      <AppSnackbar
        open={exportNotice.open}
        message={exportNotice.message}
        severity={exportNotice.severity}
        onClose={onCloseExportNotice}
      />
      <AssignmentDecisionDialog
        open={decisionDialogOpen}
        form={decisionForm}
        teamOptions={decisionTeamOptions}
        error={decisionError}
        loading={decisionLoading}
        downloading={decisionDownloading}
        downloadFormat={decisionDownloadFormat}
        onClose={onCloseDecisionDialog}
        onDownloadPdf={onDownloadAssignmentDecisionPdf}
        onDownloadWord={onDownloadAssignmentDecisionWord}
      />

      {includeApprovalDialogs && (
        <>
          <ApprovalItemsEditDialogs
            submitDialogOpen={submitDialogOpen}
            onCloseSubmitDialog={onCloseSubmitDialog}
            approvalDialogTitle={approvalDialogTitle}
            approvalActionLabel={approvalActionLabel}
            approvalSubmitInitialForm={approvalSubmitInitialForm}
            isEditingCurrentApproval={isEditingCurrentApproval}
            sections={sections}
            items={items}
            userOptions={userOptions}
            submitting={submitting}
            draftNotice={draftNotice}
            draftSelectionColumns={draftSelectionColumns}
            expandedItemIds={expandedItemIds}
            onExpandItemRows={onExpandItemRows}
            onCollapseItemRows={onCollapseItemRows}
            canSubmit={canSubmit}
            approvalSteps={approvalSteps}
            groupApprovalItemsByScope={groupApprovalItemsByScope}
            onAddDraftItem={onAddDraftItem}
            onSubmitApproval={onSubmitApproval}
            draftDialog={draftDialog}
            onCloseDraftDialog={onCloseDraftDialog}
            onSaveDraftItem={onSaveDraftItem}
            draftParentOptions={draftParentOptions}
            draftScopeItems={draftScopeItems}
            draftOfficialScopeItems={draftOfficialScopeItems}
          />

          <ApprovalInfoEditDialog
            approvalInfoDialog={approvalInfoDialog}
            onClose={onCloseApprovalInfoDialog}
            initialData={approvalInfoInitialData}
            onSubmit={onSaveApprovalInfo}
            fields={approvalInfoFields}
          />
        </>
      )}

      {includeReviewDialogs && (
        <>
          <AppDialog
            open={Boolean(reviewDialog)}
            onClose={onCloseReviewDialog}
            maxWidth="xl"
            className="subcontract-dialog"
            PaperProps={{
              sx: {
                height: "min(92vh, 1040px)",
                display: "flex",
              },
            }}
            title={reviewPhaseTitle}
            loading={submitting}
            subtitle="Kiểm tra thông tin hồ sơ và chọn kết quả phê duyệt cho từng dự toán."
            actions={
              <>
                <AppButton variant="outlined" color="inherit" onClick={onCloseReviewDialog} disabled={submitting}>Hủy</AppButton>
                <AppButton variant="contained" onClick={() => onSaveReview()} disabled={submitting}>Xác nhận phê duyệt</AppButton>
              </>
            }
          >
            <Box className="subcontract-stack">
              {reviewError && <Alert severity="error">{reviewError}</Alert>}
              <ApprovalTimeline subcontract={subcontract} approvals={reviewDialog ? [reviewDialog] : []} />

              <Box sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, pb: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 1.5 }}>Thông tin tổng quan</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.5,
                  }}
                >
                  {reviewOverviewFields.map(([label, value]) => (
                    <Box key={label} sx={{ display: "grid", gridTemplateColumns: "150px minmax(0, 1fr)", gap: 1 }}>
                      <Typography fontSize={13} color="text.secondary">{label}</Typography>
                      {label === "Trạng thái hồ sơ" && value ? (
                        <StatusChip value={value} />
                      ) : (
                        <Typography fontSize={13} fontWeight={650} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                          {displayValue(value)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography fontSize={13} color="text.secondary" sx={{ mb: 0.5 }}>Nội dung / nhận xét hồ sơ</Typography>
                  <Typography fontSize={13}>{displayValue(reviewDialog?.overallComment ?? reviewDialog?.OverallComment)}</Typography>
                </Box>
              </Box>

              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center", mb: 0.5 }}>
                  <Typography fontWeight={700}>Thông tin chi tiết</Typography>
                  <FormControlLabel
                    control={<Switch disabled={submitting} checked={showOfficialCompare} onChange={onToggleOfficialCompare} />}
                    label="So với bản chính"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Chi tiết dự toán được tách theo từng phạm vi giao khoán. Các thao tác bên dưới chỉ được lưu khi bấm xác nhận phê duyệt.
                </Typography>
                {reviewDisplayItemGroups.length === 0 ? (
                  <Alert severity="info">Chưa có chi tiết phê duyệt.</Alert>
                ) : reviewDisplayItemGroups.map((group) => (
                  <Box key={group.key} sx={{ mt: 2 }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography fontWeight={750}>{group.label}</Typography>
                      {group.subtitle && (
                        <Typography sx={{ fontSize: "12px", fontStyle: "italic" }} color="text.secondary">
                          {group.subtitle}
                        </Typography>
                      )}
                    </Box>
                    <DataTable
                      data={group.items || []}
                      columns={reviewColumns}
                      onExpandAll={() => onExpandItemRows?.(group.items || [])}
                      onCollapseAll={() => onCollapseItemRows?.(group.items || [])}
                      initialState={{
                        columnVisibility: {
                          priority: false,
                          status: false
                        },
                      }}
                      minTableWidth={1950} />
                  </Box>
                ))}
              </Box>

              <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, pt: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>Quyết định phê duyệt</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Có thể chọn nhanh toàn bộ, sau đó điều chỉnh từng dòng trước khi xác nhận.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                  <AppButton disabled={submitting} variant="outlined" size="small" startIcon={<CheckCircleOutline />} onClick={() => markAllReviewItems("Đã phê duyệt")}>Duyệt tất cả</AppButton>
                  <AppButton disabled={submitting} variant="outlined" size="small" color="inherit" startIcon={<EditNoteOutlined />} onClick={() => markAllReviewItems("Chỉnh sửa")}>Yêu cầu sửa tất cả</AppButton>
                </Box>
                <ReviewNoteField disabled={submitting} resetKey={reviewNoteResetKey} onValueChange={updateReviewNoteRef} />
              </Box>
            </Box>
          </AppDialog>

          <AppDialog
            open={false && Boolean(reviewDialog)}
            onClose={onCloseReviewDialog}
            maxWidth="lg"
            className="subcontract-dialog"
            PaperProps={{
              sx: {
                height: "min(90vh, 960px)",
                display: "flex",
              },
            }}
            title="Thực hiện phê duyệt"
            loading={submitting}
            actions={
              <>
                <AppButton color="inherit" onClick={onCloseReviewDialog} disabled={submitting}>Hủy</AppButton>
                <AppButton variant="contained" onClick={() => onSaveReview()} disabled={submitting}>Phê duyệt</AppButton>
              </>
            }
          >
            <Box className="subcontract-stack">
              {reviewError && <Alert severity="error">{reviewError}</Alert>}
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Typography fontWeight={700}>{reviewDialog?.approvalName}</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <AppButton disabled={submitting} variant="outlined" size="small" startIcon={<CheckCircleOutline />} onClick={() => markAllReviewItems("Đã phê duyệt")}>Duyệt tất cả</AppButton>
                  <AppButton disabled={submitting} variant="outlined" size="small" color="inherit" startIcon={<EditNoteOutlined />} onClick={() => markAllReviewItems("Chỉnh sửa")}>Yêu cầu sửa tất cả</AppButton>
                </Box>
              </Box>
              <ReviewNoteField disabled={submitting} label="Lý do" resetKey={reviewNoteResetKey} onValueChange={updateReviewNoteRef} />
              <DataTable
                data={sortedReviewDisplayRows}
                columns={reviewColumns}
                onExpandAll={() => onExpandItemRows?.(sortedReviewDisplayRows || [])}
                onCollapseAll={() => onCollapseItemRows?.(sortedReviewDisplayRows || [])}
                initialState={{
                  columnVisibility: {
                    priority: false,
                    status: false
                  },
                }}
                minTableWidth={1950} />

            </Box>
          </AppDialog>
        </>
      )}
    </>
  );
}
